import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, type RefObject, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { dayText } from "./accounts";
import { USER_COLUMNS } from "./columns";
import { go, useCrumb, useTitle } from "./hooks";
import { LETTER_LEGEND, letterText, matrixColumns, matrixLetter, resourceText } from "./roles";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { confirmMatches, deactivatedText, inviteProblem, isAdminGrant, preferredTruckText, reactivatedText, roleName, rolesProblem, rolesText, signInText, truckChoices } from "./users";
import { Skeleton } from "./Skeleton";

export type Failure = "hidden" | "server" | "network" | "session";

export const failureOf = (e: unknown): Failure => {
  const code = e instanceof api.ApiError ? e.status : 0;
  return code === 403 || code === 404 ? "hidden" : code === 401 ? "session" : code >= 500 ? "server" : "network";
};

const STATUS_TEXT: Record<api.Member["status"], string> = { active: "Active", deactivated: "Deactivated" };

export function Problem({ found, auth, retry }: { found: Exclude<Failure, "hidden">; auth: Auth; retry: () => void }) {
  return (
    <p role="alert" className="error">
      {found === "session" ? (
        <>
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </>
      ) : (
        <>
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={retry}>
            Try again
          </button>
        </>
      )}
    </p>
  );
}

export function Status({ saved, auth, testId, focusRef }: { saved: Saved | null; auth: Auth; testId: string; focusRef?: RefObject<HTMLParagraphElement> }) {
  return (
    <p role="status" data-testid={testId} className={saved && !saved.ok ? "error" : undefined} ref={focusRef} tabIndex={focusRef ? -1 : undefined}>
      {saved?.text}
      {saved && !saved.ok && saved.session && (
        <>
          {" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </>
      )}
    </p>
  );
}

/** The role rows of a form: a role, and the whole organization or some hubs. */
function RolePicker({ id, known, names, hubs, value, onChange }: { id: string; known: string[]; names: Record<string, string>; hubs: api.Hub[]; value: api.RoleGrant[]; onChange: (next: api.RoleGrant[]) => void }) {
  const set = (index: number, change: Partial<api.RoleGrant>) =>
    onChange(value.map((grant, i) => (i === index ? { ...grant, ...change, ...(change.scope === "tenant" ? { hub_ids: [] } : {}) } : grant)));
  return (
    <fieldset className="role-picker" data-testid={`${id}-roles`}>
      <legend>Roles</legend>
      {value.map((grant, index) => (
        <div key={index} className="role-row" data-testid="role-row">
          <label htmlFor={`${id}-role-${index}`}>Role</label>
          <select id={`${id}-role-${index}`} data-testid="role-select" value={grant.role} onChange={(e) => set(index, { role: e.target.value })}>
            {known.map((role) => (
              <option key={role} value={role}>
                {roleName(role, names)}
              </option>
            ))}
          </select>
          {hubs.length > 0 && (
            <>
              <label htmlFor={`${id}-scope-${index}`}>Where</label>
              <select id={`${id}-scope-${index}`} value={grant.scope} onChange={(e) => set(index, { scope: e.target.value as api.RoleGrant["scope"] })}>
                <option value="tenant">The whole organization</option>
                <option value="hub">Some hubs</option>
              </select>
            </>
          )}
          {grant.scope === "hub" && (
            <div className="checks">
              {hubs.map((hub) => (
                <label key={hub.id} className="check">
                  <input
                    type="checkbox"
                    checked={grant.hub_ids.includes(hub.id)}
                    onChange={(e) => set(index, { hub_ids: e.target.checked ? [...grant.hub_ids, hub.id] : grant.hub_ids.filter((h) => h !== hub.id) })}
                  />{" "}
                  {hub.name}
                </label>
              ))}
            </div>
          )}
          {value.length > 1 && (
            <button type="button" className="quiet" onClick={() => onChange(value.filter((_, i) => i !== index))}>
              Remove the role
            </button>
          )}
        </div>
      ))}
      <button type="button" data-testid={`${id}-role-add`} onClick={() => onChange([...value, { role: known.find((r) => r === "viewer") ?? known[0], scope: "tenant", hub_ids: [] }])}>
        Add a role
      </button>
    </fieldset>
  );
}

/** Design 11.5, WF-13: the members of the organization. Only an administrator sees this page (rule U-11). */
export function UserList({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [found, setFound] = useState<api.MemberList | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [inviting, setInviting] = useState(false);
  const [sort, setSort] = useSort("sort", USER_COLUMNS);
  useTitle(`Users · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then(api.members)
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="users-hidden">
        <h1 tabIndex={-1}>Users</h1>
        <p>Only an administrator of the whole organization sees the users.</p>
      </main>
    );
  }
  const list = typeof found === "object" ? found : null;
  return (
    <main className="users-page wide" data-testid="users-page">
      <h1 tabIndex={-1}>Users</h1>
      <p className="sub">Each member signs in with the email here. A role gives the member access; a member with no role sees nothing.</p>
      {list && !inviting && (
        <div className="tabs">
          <button type="button" className="primary" data-testid="user-invite" onClick={() => setInviting(true)}>
            Invite a member
          </button>
        </div>
      )}
      {list && inviting && <InviteForm base={base} list={list} auth={auth} user={user} onCancel={() => setInviting(false)} />}
      {found === undefined ? (
        <Skeleton shape="table" columns={4} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        <>
          <SortSelect id="users-select" columns={USER_COLUMNS} sort={sort} onSort={setSort} fallback="Email A to Z" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Users">
            <table className="list" data-testid="user-table" data-cols="users-select">
              <thead>
                <tr>
                  {USER_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(found.members, USER_COLUMNS, sort).map((row) => (
                  <tr key={row.id} data-testid="user-row" data-email={row.email} className={row.status === "deactivated" ? "muted" : undefined}>
                    <th scope="row">
                      <a href={`${base}/users/${row.id}`} onClick={go} data-testid="user-link">
                        {row.name || row.email}
                      </a>
                      {row.me && <span className="sub"> (you)</span>}
                      {row.name && <span className="sub">{row.email}</span>}
                    </th>
                    <td data-label="Email">{row.email}</td>
                    <td data-label="Roles">{rolesText(row.roles, found.hubs, found.role_names)}</td>
                    <td data-label="Last sign-in" title={row.last_sign_in ?? undefined}>
                      {signInText(row.last_sign_in)}
                    </td>
                    <td data-label="Status">
                      {row.status === "deactivated" ? <span className="chip-flag error">{STATUS_TEXT[row.status]}</span> : STATUS_TEXT[row.status]}
                    </td>
                    <td data-label="Preferred truck" data-testid="user-truck">
                      {row.preferred_truck ?? "–"}
                    </td>
                    <td data-label="Added">{dayText(row.created_at.slice(0, 10), true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <RoleMatrix base={base} members={found.members} user={user} auth={auth} />
        </>
      )}
    </main>
  );
}

/** What each role can do: one row for each module, one column for each role (rule U-12). */
function RoleMatrix({ base, members, user, auth }: { base: string; members: api.Member[]; user: User; auth: Auth }) {
  const [found, setFound] = useState<api.RoleList | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(api.roles)
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt]);
  const columns = typeof found === "object" ? matrixColumns(found, members, (role) => roleName(role)) : [];
  return (
    <details className="users-role-matrix" data-testid="users-role-matrix" open>
      <summary>
        <h2>What each role can do</h2>
      </summary>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : found === "hidden" ? (
        <p className="sub">Only an administrator of the whole organization sees the roles.</p>
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        <>
          <dl className="matrix-legend" data-testid="users-role-legend">
            {LETTER_LEGEND.map(([letter, text]) => (
              <div key={letter}>
                <dt>{letter}</dt>
                <dd>{text}</dd>
              </div>
            ))}
          </dl>
          <div className="matrix-scroll" tabIndex={0} role="region" aria-label="What each role can do">
            <table className="role-grid" data-testid="users-role-table">
              <thead>
                <tr>
                  <th scope="col">Module</th>
                  {columns.map((column) => (
                    <th scope="col" key={column.key} data-testid="users-role-column" data-role={column.key} data-kind={column.kind}>
                      {column.id ? (
                        <a href={`${base}/roles/${column.id}`} onClick={go}>
                          {column.name}
                        </a>
                      ) : (
                        column.name
                      )}
                      <span className="sub">
                        {column.kind === "custom" ? "Custom, " : ""}
                        {column.members === 1 ? "1 member" : `${column.members} members`}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {found.resources.map((resource) => (
                  <tr key={resource} data-testid="users-role-module" data-module={resource}>
                    <th scope="row">{resourceText(resource)}</th>
                    {columns.map((column) => (
                      <td key={column.key} title={`${column.name}: ${letterText(column.letters[resource])}`} className={column.letters[resource] ? undefined : "none"}>
                        {matrixLetter(column.letters[resource])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="sub">
            A member with more than one role gets the access of each role.{" "}
            <a href={`${base}/roles`} onClick={go}>
              Go to the roles
            </a>
          </p>
        </>
      )}
    </details>
  );
}

function InviteForm({ base, list, auth, user, onCancel }: { base: string; list: api.MemberList; auth: Auth; user: User; onCancel: () => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roles, setRoles] = useState<api.RoleGrant[]>([{ role: "viewer", scope: "tenant", hub_ids: [] }]);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const statusLine = useRef<HTMLParagraphElement>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = inviteProblem(email, roles, list.role_names);
    if (problem) return setSaved({ ok: false, text: problem });
    setSaving(true);
    setSaved(null);
    try {
      const made = await api.inviteMember(await user.getIdToken(), { email: email.trim(), name: name.trim(), roles });
      history.pushState(null, "", `${base}/users/${made.id}${made.mail_sent ? "" : "#no-mail"}`);
      dispatchEvent(new PopStateEvent("popstate"));
    } catch (e) {
      setSaved(saveFailure(e, "The invitation"));
      requestAnimationFrame(() => statusLine.current?.focus());
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="invite-form" aria-labelledby="invite-title">
      <h2 id="invite-title">Invite a member</h2>
      <p className="sub">The member gets an email with a sign-in link. The member can also sign in with Google or Microsoft, if the organization allows it.</p>
      <label htmlFor="invite-email">Email</label>
      <input id="invite-email" data-testid="invite-email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
      <label htmlFor="invite-name">Name (optional)</label>
      <input id="invite-name" data-testid="invite-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
      <RolePicker id="invite" known={list.known_roles} names={list.role_names} hubs={list.hubs} value={roles} onChange={setRoles} />
      <div className="actions">
        <button type="submit" className="primary" data-testid="invite-send" disabled={saving}>
          Invite
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <Status saved={saved} auth={auth} testId="invite-status" focusRef={statusLine} />
    </form>
  );
}

/** One member: its roles, the hubs of each role, and Deactivate (rule U-11). */
export function UserPage({ id, base, org, auth, user }: { id: string; base: string; org: api.Bootstrap; auth: Auth; user: User }) {
  const [found, setFound] = useState<api.MemberDetail | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [mode, setMode] = useState<"view" | "roles" | "name" | "truck" | "deactivate" | "reactivate">("view");
  const [truck, setTruck] = useState("");
  const [roles, setRoles] = useState<api.RoleGrant[]>([]);
  const [name, setName] = useState("");
  const [typed, setTyped] = useState("");
  const [saved, setSaved] = useState<Saved | null>(() =>
    location.hash === "#no-mail" ? { ok: true, text: "Invited. No email was sent: give the member the address of this site." } : null,
  );
  const [saving, setSaving] = useState(false);
  const statusLine = useRef<HTMLParagraphElement>(null);
  const detail = typeof found === "object" ? found : null;
  useTitle(`${detail ? detail.name || detail.email : "User"} · ${org.name}`);
  useCrumb(detail ? detail.name || detail.email : null);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.member(token, id))
      .then((next) => live && setFound(next))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, id, attempt]);
  if (found === "hidden") {
    return (
      <main data-testid="user-missing">
        <h1 tabIndex={-1}>User not found</h1>
        <p>
          No member at this link is visible to you.{" "}
          <a href={`${base}/users`} onClick={go}>
            Go to the users
          </a>
        </p>
      </main>
    );
  }
  if (!detail) {
    return <main>{typeof found === "string" ? <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} /> : <Skeleton shape="detail" />}</main>;
  }
  const merge = (next: api.Member) => setFound({ ...detail, ...next });
  const run = async <T extends api.Member>(action: () => Promise<T>, done: string | ((reply: T) => Saved), thing: string) => {
    setSaving(true);
    setSaved(null);
    try {
      const reply = await action();
      merge(reply);
      setMode("view");
      setSaved(typeof done === "string" ? { ok: true, text: done } : done(reply));
    } catch (e) {
      setSaved(saveFailure(e, thing));
    } finally {
      setSaving(false);
      requestAnimationFrame(() => statusLine.current?.focus());
    }
  };
  const token = () => user.getIdToken();
  const open = (next: typeof mode, prepare: () => void) => {
    prepare();
    setMode(next);
    setSaved(null);
  };
  const saveRoles = (event: FormEvent) => {
    event.preventDefault();
    const problem = rolesProblem(roles, detail.role_names);
    if (problem) return setSaved({ ok: false, text: problem });
    if (mode === "reactivate") {
      void run(async () => api.reactivateMember(await token(), id, roles), reactivatedText, "The change");
    } else {
      void run(async () => api.changeMember(await token(), id, { roles }), "Saved: the roles changed.", "The roles");
    }
  };
  const active = detail.status === "active";
  const selfAdmin = detail.me && isAdminGrant(detail.roles);
  return (
    <main className="user-page" data-testid="user-page" data-status={detail.status}>
      <h1 tabIndex={-1}>{detail.name || detail.email}</h1>
      <dl className="facts" data-testid="user-facts">
        <dt>Email</dt>
        <dd>{detail.email}</dd>
        <dt>Roles</dt>
        <dd data-testid="user-roles">{rolesText(detail.roles, detail.hubs, detail.role_names)}</dd>
        <dt>Status</dt>
        <dd data-testid="user-status">
          {STATUS_TEXT[detail.status]}
          {detail.deactivated_at && ` since ${dayText(detail.deactivated_at.slice(0, 10), true)}`}
        </dd>
        <dt>Last sign-in</dt>
        <dd>{signInText(detail.last_sign_in)}</dd>
        {(detail.can_drive || detail.preferred_truck) && (
          <>
            <dt>Preferred truck</dt>
            <dd data-testid="user-preferred-truck">{preferredTruckText(detail, detail.trucks)}</dd>
          </>
        )}
      </dl>
      {mode === "view" && (
        <div className="tabs">
          {active && (
            <button type="button" data-testid="user-roles-change" onClick={() => open("roles", () => setRoles(detail.roles))}>
              Change the roles
            </button>
          )}
          <button type="button" data-testid="user-name-change" onClick={() => open("name", () => setName(detail.name))}>
            Change the name
          </button>
          {active && (detail.can_drive || detail.preferred_truck) && (
            <button type="button" data-testid="user-truck-change" onClick={() => open("truck", () => setTruck(detail.preferred_truck_id ?? ""))}>
              Change the preferred truck
            </button>
          )}
          {active && !detail.me && (
            <button type="button" className="danger" data-testid="user-deactivate" onClick={() => open("deactivate", () => setTyped(""))}>
              Deactivate
            </button>
          )}
          {!active && (
            <button
              type="button"
              className="primary"
              data-testid="user-reactivate"
              onClick={() => open("reactivate", () => setRoles([{ role: "viewer", scope: "tenant", hub_ids: [] }]))}
            >
              Make active again
            </button>
          )}
        </div>
      )}
      {(mode === "roles" || mode === "reactivate") && (
        <form onSubmit={saveRoles} noValidate data-testid="user-roles-form">
          {selfAdmin && <p className="sub">You cannot remove your own administrator role. Another administrator can.</p>}
          <RolePicker id="member" known={detail.known_roles} names={detail.role_names} hubs={detail.hubs} value={roles} onChange={setRoles} />
          <div className="actions">
            <button type="submit" className="primary" data-testid="user-roles-save" disabled={saving}>
              {mode === "reactivate" ? "Make active" : "Save the roles"}
            </button>
            <button type="button" onClick={() => setMode("view")}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {mode === "name" && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => api.changeMember(await token(), id, { name: name.trim() }), "Saved: the name changed.", "The name");
          }}
          noValidate
          className="inline-form"
          data-testid="user-name-form"
        >
          <label htmlFor="user-name">Name</label>
          <input id="user-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="primary" disabled={saving}>
            Save
          </button>
          <button type="button" onClick={() => setMode("view")}>
            Cancel
          </button>
        </form>
      )}
      {mode === "truck" && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(
              async () => api.changeMember(await token(), id, { preferred_truck_id: truck || null }),
              truck ? "Saved: the preferred truck changed." : "Saved: the member has no preferred truck.",
              "The preferred truck",
            );
          }}
          noValidate
          className="inline-form"
          data-testid="user-truck-form"
        >
          <label htmlFor="user-truck">Preferred truck</label>
          <select id="user-truck" data-testid="user-truck-select" value={truck} onChange={(e) => setTruck(e.target.value)}>
            <option value="">No preferred truck</option>
            {truckChoices(detail.trucks, detail.preferred_truck_id).map((choice) => (
              <option key={choice.id} value={choice.id}>
                {choice.active ? choice.code : `${choice.code} (inactive)`}
              </option>
            ))}
          </select>
          <button type="submit" className="primary" disabled={saving}>
            Save
          </button>
          <button type="button" onClick={() => setMode("view")}>
            Cancel
          </button>
        </form>
      )}
      {mode === "deactivate" && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!confirmMatches(typed, detail.email)) return setSaved({ ok: false, text: "Type the email of the member to deactivate it." });
            void run(async () => api.deactivateMember(await token(), id), deactivatedText, "The change");
          }}
          noValidate
          data-testid="user-deactivate-form"
        >
          <p>
            Deactivate {detail.email}? The member loses all roles and cannot sign in. The history of the member stays.
          </p>
          <label htmlFor="user-confirm">Type the email to continue</label>
          <input id="user-confirm" data-testid="user-confirm" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
          <div className="actions">
            <button type="submit" className="danger" data-testid="user-deactivate-confirm" disabled={saving}>
              Deactivate
            </button>
            <button type="button" onClick={() => setMode("view")}>
              Cancel
            </button>
          </div>
        </form>
      )}
      <Status saved={saved} auth={auth} testId="user-status-line" focusRef={statusLine} />
      <p>
        <a href={`${base}/users`} onClick={go}>
          Go to the users
        </a>
      </p>
    </main>
  );
}
